/**
 * Schedule Message Modal Component
 * Modal for scheduling messages for future delivery
 */

import { useState, useEffect } from 'react';
import { scheduledMessagesService } from '@/services/scheduled-messages.service';
import { useDialogA11y } from '@/hooks/useDialogA11y';

interface ScheduleMessageModalProps {
  content: string;
  channelId?: string | null;
  dmThreadId?: string | null;
  onClose: () => void;
  onScheduled: () => void;
}

const ScheduleMessageModal = ({
  content,
  channelId,
  dmThreadId,
  onClose,
  onScheduled,
}: ScheduleMessageModalProps) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { dialogRef, titleId } = useDialogA11y(onClose);

  // Get user's timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Initialize with current date and time + 1 hour
  useEffect(() => {
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
    
    // Format date as YYYY-MM-DD
    const year = oneHourLater.getFullYear();
    const month = String(oneHourLater.getMonth() + 1).padStart(2, '0');
    const day = String(oneHourLater.getDate()).padStart(2, '0');
    setDate(`${year}-${month}-${day}`);
    
    // Format time as HH:MM
    const hours = String(oneHourLater.getHours()).padStart(2, '0');
    const minutes = String(oneHourLater.getMinutes()).padStart(2, '0');
    setTime(`${hours}:${minutes}`);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!date || !time) {
      setError('Датата и часът са задължителни');
      return;
    }

    // Combine date and time into ISO 8601 format
    const scheduledDateTime = new Date(`${date}T${time}:00`);
    
    // Check if date is in the future
    if (scheduledDateTime <= new Date()) {
      setError('Планираната дата трябва да е в бъдещето');
      return;
    }

    setIsSubmitting(true);

    try {
      await scheduledMessagesService.createScheduledMessage({
        content,
        scheduledFor: scheduledDateTime.toISOString(),
        timezone: userTimezone,
        channelId: channelId || undefined,
        dmThreadId: dmThreadId || undefined,
      });

      onScheduled();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Грешка при планиране на съобщение');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-50" onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="bg-white dark:bg-dark-bg-elevated rounded-lg shadow-xl max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border-primary">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
            Планирай съобщение
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 dark:text-dark-text-muted hover:text-gray-600 dark:hover:text-dark-text-secondary rounded transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Message Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Съобщение
            </label>
            <p className="text-sm text-gray-600 dark:text-dark-text-muted bg-gray-50 dark:bg-dark-bg-tertiary p-3 rounded border border-gray-200 dark:border-dark-border-secondary">
              {content}
            </p>
          </div>

          {/* Date Input */}
          <div>
            <label
              htmlFor="schedule-date"
              className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1"
            >
              Дата *
            </label>
            <input
              type="date"
              id="schedule-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-secondary dark:bg-dark-bg-input dark:text-dark-text-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Time Input */}
          <div>
            <label
              htmlFor="schedule-time"
              className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1"
            >
              Час *
            </label>
            <input
              type="time"
              id="schedule-time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-secondary dark:bg-dark-bg-input dark:text-dark-text-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Timezone Display */}
          <p className="text-xs text-gray-500 dark:text-dark-text-muted">
            Часова зона: {userTimezone}
          </p>

          {/* Error Message */}
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 dark:text-dark-text-secondary bg-gray-100 dark:bg-dark-bg-tertiary hover:bg-gray-200 dark:hover:bg-dark-bg-hover rounded-lg transition-colors"
              disabled={isSubmitting}
            >
              Отказ
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Планиране...' : 'Планирай'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleMessageModal;
